// src/pages/KundenDetails.jsx
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../constants/api";
import { useConfirm } from "../components/ui/ConfirmationModal.jsx";
import { useAuth } from "../components/ui/AuthContext.jsx";
import { promise, handleError } from "../utils/toast.js";
import { smartBack, navigateToList } from "../constants/nav";
import { invalidateListDomain } from "../src/hooks/listQueryCache.js";
import { canSeeLeitstand } from "../constants/roles.js";
import AppButton from "../components/ui/AppButton.jsx";
import { detailPageHeaderNavClassName, detailPageHeaderNavActionsClassName } from "../design/tokens.js";
import KundenBlockAdresse from "../modules/kunden/components/KundenBlockAdresse.jsx";
import SharePointEntityLink from "../components/sharepoint/SharePointEntityLink.jsx";
import DmsTreePanel from "../components/dms/DmsTreePanel.jsx";
import KundenBlockUploads from "../modules/kunden/components/KundenBlockUploads.jsx";
import KundenBlockObjekte from "../modules/kunden/components/KundenBlockObjekte.jsx";
import KundenBlockKommentare from "../modules/kunden/components/KundenBlockKommentare.jsx";
import KundenBlockPendenzen from "../modules/kunden/components/KundenBlockPendenzen.jsx";

const PAGE_GRAD = "min-h-screen w-full bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
const FRAME = "app-frame";

export default function KundenDetails() {
  const { id } = useParams();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kunde, setKunde] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [pendenzenOpenCount, setPendenzenOpenCount] = useState(0);
  const mountedRef = useRef(true);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    const ctrl = new AbortController();
    abortRef.current?.abort?.();
    abortRef.current = ctrl;
    try {
      setLoading(true);
      setLoadError(null);
      const res = await api.get(`/kunden/${id}`, { signal: ctrl.signal, silent: true });
      if (!mountedRef.current) return;
      const data = res.data || null;
      setKunde(data);
      const label =
        data?.firma?.trim() ||
        [data?.vorname, data?.nachname].filter(Boolean).join(" ").trim() ||
        `Kunde #${id}`;
      document.title = `${label} • Kunden • WireTrack`;
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      if (!mountedRef.current) return;
      console.error("Fehler beim Laden:", err);
      setKunde(null);
      if (err?.response?.status === 404) {
        setLoadError("not_found");
        return;
      }
      setLoadError("error");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [id, navigate]);

  const loadPendenzen = useCallback(async (kundenId) => {
    try {
      const pe = await api.get(`/pendenzen/exists`, {
        params: { kunde_id: kundenId, include_projects: "1" },
      });
      setPendenzenOpenCount(Number(pe?.data?.count || 0));
    } catch {
      setPendenzenOpenCount(0);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    window.scrollTo(0, 0);
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort?.();
    };
  }, [load]);

  useEffect(() => {
    if (kunde?.id) loadPendenzen(kunde.id);
  }, [kunde?.id, loadPendenzen]);

  const titleText = useMemo(() => {
    if (!kunde) return "Kunde";
    return (
      kunde.firma?.trim() ||
      [kunde.vorname, kunde.nachname].filter(Boolean).join(" ").trim() ||
      "Kunde"
    );
  }, [kunde]);

  const handleBearbeiten = () => navigate(`/kunden/${id}/bearbeiten`);

  const handleLoeschen = async () => {
    if (deleteBusy) return;
    const name =
      kunde?.firma ||
      `${kunde?.vorname || ""} ${kunde?.nachname || ""}`.trim() ||
      `Kunde #${id}`;
    const { ok } = await confirm({
      title: "Kunden löschen?",
      message: `„${name}" wird endgültig gelöscht (kein Undo). Fortfahren?`,
      variant: "danger",
      confirmLabel: "Löschen",
      cancelLabel: "Abbrechen",
    });
    if (!ok) return;
    setDeleteBusy(true);
    try {
      await promise(api.delete(`/kunden/${id}`), {
        loading: "Lösche…",
        success: "ok.deleted",
        error: "srv.error",
      });
      invalidateListDomain("kunden");
      navigateToList(navigate, "/kunden", { replace: true });
    } catch (err) {
      handleError(err);
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={PAGE_GRAD}>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className={`rounded-2xl h-20 mb-3 animate-pulse border ${FRAME}`} />
          <div className="grid gap-3">
            <div className={`rounded-2xl h-40 animate-pulse border ${FRAME}`} />
            <div className={`rounded-2xl h-14 animate-pulse border ${FRAME}`} />
            <div className={`rounded-2xl h-14 animate-pulse border ${FRAME}`} />
          </div>
        </div>
      </div>
    );
  }

  if (!kunde && loadError === "not_found") {
    return (
      <div className={PAGE_GRAD}>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className={`rounded-2xl p-5 border ${FRAME}`}>
            <h1 className="text-lg font-semibold text-blue-100/95">Kunde nicht gefunden</h1>
            <p className="mt-2 text-sm text-blue-200/80 leading-relaxed max-w-xl">
              Der Kunde existiert nicht oder wurde gelöscht.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                onClick={() => smartBack(navigate, ["/kunden", "/projekte", "/service", "/"])}
              >
                Zurück
              </AppButton>
              <AppButton type="button" variant="primary" size="lg" onClick={load}>
                Erneut laden
              </AppButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!kunde && loadError === "error") {
    return (
      <div className={PAGE_GRAD}>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className={`rounded-2xl p-5 border ${FRAME}`}>
            <h1 className="text-lg font-semibold text-blue-100/95">Kundendaten konnten nicht geladen werden</h1>
            <p className="mt-2 text-sm text-blue-200/80 leading-relaxed max-w-xl">
              Die Kundendaten sind momentan nicht verfügbar. Bitte prüfe die Verbindung und versuche es erneut.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                onClick={() => smartBack(navigate, ["/kunden", "/projekte", "/service", "/"])}
              >
                Zurück
              </AppButton>
              <AppButton type="button" variant="primary" size="lg" onClick={load}>
                Erneut laden
              </AppButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!kunde) return null;

  const showPendenzen = pendenzenOpenCount > 0;

  return (
    <div className={PAGE_GRAD}>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="grid grid-cols-1 gap-3">
          <div className={`rounded-2xl px-4 py-3 border flex flex-col sm:flex-row sm:items-center sm:justify-between ${FRAME}`}>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2 text-blue-100/90 min-w-0">
                <span aria-hidden>👥</span>
                <span className="truncate">{titleText}</span>
              </h1>
              {kunde.id ? (
                <p className="text-xs text-blue-200/60 mt-0.5 tabular-nums">#{kunde.id}</p>
              ) : null}
            </div>

            <div className={`${detailPageHeaderNavActionsClassName} mt-3 sm:mt-0`}>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                onClick={() => smartBack(navigate, ["/kunden", "/projekte", "/service", "/"])}
              >
                Zurück
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                onClick={() => navigateToList(navigate, "/kunden")}
              >
                Kundenliste
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                onClick={() => navigate("/meine-aufgaben")}
              >
                Meine Aufgaben
              </AppButton>
              {canSeeLeitstand(user) && (
                <AppButton
                  type="button"
                  variant="secondary"
                  size="lg"
                  className={detailPageHeaderNavClassName}
                  onClick={() => navigate("/leitstand/alle")}
                >
                  Übersicht
                </AppButton>
              )}
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                onClick={() => navigate("/profil")}
              >
                Mein Profil
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                onClick={() => navigate("/")}
              >
                Dashboard
              </AppButton>
            </div>
          </div>

          <KundenBlockAdresse kunde={kunde} onBearbeiten={handleBearbeiten} onLoeschen={handleLoeschen} />

          <div className="px-1 space-y-2">
            <SharePointEntityLink entityType="kunde" entityId={kunde.id} />
            <DmsTreePanel entityKind="kunde" entityId={kunde.id} />
          </div>

          {showPendenzen && (
            <KundenBlockPendenzen kundenId={kunde.id} onEmpty={() => setPendenzenOpenCount(0)} />
          )}

          <KundenBlockObjekte kundenId={kunde.id} />
          <KundenBlockUploads kundenId={kunde.id} />
          <KundenBlockKommentare kundenId={kunde.id} />
        </div>
      </div>
    </div>
  );
}
